import type { GenerationProjectSummaryDto } from '@mpr/shared';

import type { GenerationJob, GenerationJobStatus, VideoPlan } from './api.js';

/**
 * The studio's project logic that has no screen of its own: which output is
 * the one to publish, what travels to the composer, and what a scheduled post
 * will accept. Kept apart from the components so each rule can be tested
 * without rendering anything.
 */

/* -------------------------------------------------------------------------- */
/*  What travels from the studio to the composer                               */
/* -------------------------------------------------------------------------- */

export type HandoffMediaKind = 'image' | 'video';

export interface HandoffAsset {
  id: string;
  filename: string;
  /** Lets the composer open on the right post type; absent means "a picture". */
  mediaKind?: HandoffMediaKind;
}

/**
 * Everything the studio hands to Publish.
 *
 * `message` fills the composer's text field; `projectId` is carried through
 * the composer so that, once the post is queued, the project can be told
 * where its output went.
 */
export interface StudioHandoff {
  assets: HandoffAsset[];
  message?: string;
  projectId?: string;
  /**
   * The project's chosen video aspect ("9:16", "16:9"...), when it has one. Only
   * a hint for the composer's opening post type; it never publishes anything.
   */
  aspect?: string | null;
}

/** A finished file that could go into a post, from a job or from a video plan. */
export interface PublishableMedia {
  /** Unique within a project: `job:<id>` or `plan:<id>`. */
  key: string;
  mediaId: string;
  kind: 'image' | 'video' | 'audio';
  filename: string;
  approved: boolean;
  updatedAt: string;
}

export function isFinished(status: GenerationJobStatus): boolean {
  return status === 'DONE' || status === 'APPROVED';
}

/** The media a finished job produced, or null when it made none (text) or is not finished. */
export function mediaOfJob(job: GenerationJob): PublishableMedia | null {
  if (!isFinished(job.status) || !job.resultMediaId) return null;
  const kind = job.capability === 'VIDEO' ? 'video' : job.capability === 'AUDIO' ? 'audio' : 'image';
  return {
    key: `job:${job.id}`,
    mediaId: job.resultMediaId,
    kind,
    filename: `${job.capability.toLowerCase()}-${job.id}`,
    approved: job.status === 'APPROVED',
    updatedAt: job.updatedAt,
  };
}

/** A finished long video. Plans have no approval step, so they never count as approved. */
export function mediaOfPlan(plan: VideoPlan): PublishableMedia | null {
  if (plan.status !== 'DONE' || !plan.resultMediaId) return null;
  return {
    key: `plan:${plan.id}`,
    mediaId: plan.resultMediaId,
    kind: 'video',
    filename: `video-plan-${plan.id}`,
    approved: false,
    updatedAt: plan.updatedAt,
  };
}

/** Approved beats unapproved; among equals, the newest. Negative when `a` should come first. */
function byPreference(a: { approved: boolean; updatedAt: string }, b: { approved: boolean; updatedAt: string }) {
  if (a.approved !== b.approved) return a.approved ? -1 : 1;
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

/** The text to send by default: the newest approved one, else the newest finished one. */
export function pickDefaultTextJob(textJobs: GenerationJob[]): GenerationJob | null {
  const candidates = textJobs
    .filter((job) => isFinished(job.status) && (job.resultText ?? '').trim().length > 0)
    .map((job) => ({ job, approved: job.status === 'APPROVED', updatedAt: job.updatedAt }))
    .sort(byPreference);
  return candidates[0]?.job ?? null;
}

/**
 * The media to send by default.
 *
 * A post carries photos or one video, never both, so this picks one file:
 * the best image and the best video compete, approval deciding first and
 * recency second. Audio is never chosen — a post cannot carry it alone.
 */
export function pickDefaultMediaKeys(media: PublishableMedia[]): string[] {
  const best = (kind: 'image' | 'video') => media.filter((m) => m.kind === kind).sort(byPreference)[0];
  const winner = [best('image'), best('video')]
    .filter((m): m is PublishableMedia => m !== undefined)
    .sort(byPreference)[0];
  return winner ? [winner.key] : [];
}

/**
 * Tick or untick one file, keeping the selection something a post can carry:
 * a video travels alone, and photos cannot join a video.
 */
export function toggleMediaKey(selected: string[], key: string, all: PublishableMedia[]): string[] {
  if (selected.includes(key)) return selected.filter((entry) => entry !== key);
  const added = all.find((m) => m.key === key);
  if (!added || added.kind === 'audio') return selected;
  if (added.kind === 'video') return [key];
  const kept = selected.filter((entry) => all.find((m) => m.key === entry)?.kind === 'image');
  return [...kept, key];
}

/** Assemble what Publish receives. Audio is left out: the composer has no audio post. */
export function buildHandoff(input: {
  projectId?: string | null;
  message?: string | null;
  media: PublishableMedia[];
  aspect?: string | null;
}): StudioHandoff {
  const assets: HandoffAsset[] = input.media
    .filter((m) => m.kind !== 'audio')
    .map((m) => ({
      id: m.mediaId,
      filename: m.filename,
      mediaKind: m.kind === 'video' ? ('video' as const) : ('image' as const),
    }));
  const message = input.message?.trim();
  return {
    assets,
    ...(message ? { message } : {}),
    ...(input.projectId ? { projectId: input.projectId } : {}),
    ...(input.aspect ? { aspect: input.aspect } : {}),
  };
}

export type ComposerKind = 'TEXT' | 'PHOTO' | 'MULTI_PHOTO' | 'VIDEO' | 'REEL';

/**
 * The post type the composer should open on for a handoff.
 *
 * A single video opens as a Reel when the project says 9:16, or says nothing
 * (no better information); a project that chose another aspect stays a plain
 * video. A suggestion only: the composer lets the operator change it.
 */
export function composerKindFor(assets: HandoffAsset[], aspect?: string | null): ComposerKind {
  if (assets.length === 0) return 'TEXT';
  if (assets.some((asset) => asset.mediaKind === 'video')) {
    return assets.length === 1 && (!aspect || aspect === '9:16') ? 'REEL' : 'VIDEO';
  }
  return assets.length > 1 ? 'MULTI_PHOTO' : 'PHOTO';
}

/** "Also publish as a Story" starts ticked on a suggested Reel, and nowhere else. */
export function suggestedAlsoAs(kind: ComposerKind): { story: boolean; reel: boolean } {
  return { story: kind === 'REEL', reel: false };
}

/* -------------------------------------------------------------------------- */
/*  Attaching to a post that is already scheduled                              */
/* -------------------------------------------------------------------------- */

/** A post carries at most this many files; the same cap `editJobSchema` enforces. */
export const MAX_POST_MEDIA = 10;

export type AttachBlock = 'NOTHING_SELECTED' | 'POST_HAS_VIDEO' | 'VIDEO_NEEDS_EMPTY_POST' | 'TOO_MANY';

/**
 * May these files be added to this post?
 *
 * `existing` are the files the post holds; `externalImages` are pictures
 * placed by address (an outside agent's), which count toward the post just the
 * same. A video must be the post's only file, in either direction.
 */
export function checkAttach(
  existing: Array<{ mimeType: string }>,
  externalImages: number,
  incoming: PublishableMedia[],
): { ok: true } | { ok: false; reason: AttachBlock } {
  const files = incoming.filter((m) => m.kind !== 'audio');
  if (files.length === 0) return { ok: false, reason: 'NOTHING_SELECTED' };

  const postHasVideo = existing.some((file) => file.mimeType.startsWith('video/'));
  if (postHasVideo) return { ok: false, reason: 'POST_HAS_VIDEO' };

  const existingCount = existing.length + externalImages;
  if (files.some((m) => m.kind === 'video') && (existingCount > 0 || files.length > 1)) {
    return { ok: false, reason: 'VIDEO_NEEDS_EMPTY_POST' };
  }
  if (existingCount + files.length > MAX_POST_MEDIA) return { ok: false, reason: 'TOO_MANY' };
  return { ok: true };
}

/** The post's current files followed by the new ones, none twice — `PATCH` replaces the whole list. */
export function mergeMediaIds(existingIds: string[], incomingIds: string[]): string[] {
  return [...new Set([...existingIds, ...incomingIds])];
}

/* -------------------------------------------------------------------------- */
/*  A project's outputs, grouped                                               */
/* -------------------------------------------------------------------------- */

/** The newest version of one deliverable and the versions it replaced, newest first. */
export interface OutputChain {
  latest: GenerationJob;
  earlier: GenerationJob[];
}

export interface ProjectOutputs {
  text: OutputChain[];
  image: OutputChain[];
  audio: OutputChain[];
  video: OutputChain[];
}

/**
 * Fold a project's flat job list into one entry per deliverable.
 *
 * A revision is a new job pointing at the one it replaced, so the head of a
 * chain is the job nothing else points to. Heads are listed newest first.
 */
export function groupOutputs(jobs: GenerationJob[]): ProjectOutputs {
  const byId = new Map(jobs.map((job) => [job.id, job]));
  const replaced = new Set(jobs.map((job) => job.parentJobId).filter((id): id is string => id !== null));

  const chains = jobs
    .filter((job) => !replaced.has(job.id))
    .map((latest): OutputChain => {
      const earlier: GenerationJob[] = [];
      const seen = new Set([latest.id]);
      let cursor = latest.parentJobId ? byId.get(latest.parentJobId) : undefined;
      // `seen` guards a malformed cycle; a real chain is a straight line.
      while (cursor && !seen.has(cursor.id)) {
        earlier.push(cursor);
        seen.add(cursor.id);
        cursor = cursor.parentJobId ? byId.get(cursor.parentJobId) : undefined;
      }
      return { latest, earlier };
    })
    .sort((a, b) => new Date(b.latest.updatedAt).getTime() - new Date(a.latest.updatedAt).getTime());

  const of = (capability: GenerationJob['capability']) => chains.filter((c) => c.latest.capability === capability);
  return { text: of('SCRIPT'), image: of('IMAGE'), audio: of('AUDIO'), video: of('VIDEO') };
}

/* -------------------------------------------------------------------------- */
/*  The archive card                                                           */
/* -------------------------------------------------------------------------- */

export type OutputKind = 'text' | 'image' | 'audio' | 'video';

const BADGE_ORDER: OutputKind[] = ['text', 'image', 'audio', 'video'];

/** Which count badges a project card shows (only the non-zero ones) and its two state flags. */
export function projectBadges(project: Pick<GenerationProjectSummaryDto, 'counts' | 'inProgress' | 'published'>): {
  counts: Array<{ kind: OutputKind; count: number }>;
  inProgress: boolean;
  published: boolean;
} {
  return {
    counts: BADGE_ORDER.filter((kind) => project.counts[kind] > 0).map((kind) => ({
      kind,
      count: project.counts[kind],
    })),
    inProgress: project.inProgress,
    published: project.published,
  };
}

/** Whether a loaded project still has something running, so its view keeps refreshing. */
export function projectIsMoving(project: { jobs: GenerationJob[]; plans: VideoPlan[] } | undefined): boolean {
  if (!project) return false;
  return (
    project.jobs.some((job) => job.status === 'PENDING' || job.status === 'RUNNING') ||
    project.plans.some((plan) => plan.status === 'RUNNING')
  );
}
